import { Collapse, IconButton } from '@mui/material';
import * as React from 'react';
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { MdChevronRight, MdExpandMore } from 'react-icons/md';
import { CheckboxField } from 'src/shared/index';
import { InputField, TextButton } from 'src/shared/ui/elements';
import * as Format from 'src/shared/utils/format';
import * as Yup from 'yup';
import Tooltip from '@mui/material/Tooltip';

import InfoIcon from '@mui/icons-material/Info';
const NotificationTypes = {
  CLASS_NOTIFICATION: 'classNotification',
  NEWS_ANNOUNCEMENT: 'newsOrAnnouncement',
  EMAIL_NEWS_ANNOUNCEMENT: 'emailNewsOrAnnouncement',
  EMAIL_CLASS_NOTIFICATION: 'emailClassNotification',
};
export const CheckboxWithConsent = ({
  label,
  hoverLabel,
  isTextConsent = false,
  ...props
}) => {
  return (
    <div className="tw-mb-1">
      <CheckboxField
        {...props}
        labelClassName="!tw-mb-0"
        className="!tw-mb-1"
        $label={
          <span className="flex tw-items-center tw-gap-1">
            <span className="text-sm">{label}</span>
            {hoverLabel && (
              <Tooltip title={hoverLabel} placement="top">
                <InfoIcon sx={{ fontSize: 13 }} />
              </Tooltip>
            )}
          </span>
        }
      />
      {isTextConsent && (
        <p className="tw-text-[10px] tw-text-gray-600 tw-mt-1 tw-ml-6">
          By checking this box, you consent to receive text message/SMS updates
          from Soccer Stars, Amazing Athletes, TGA - Youth Athletes United and
          their subsidiaries. This includes marketing communications, relevant
          information regarding your requests, and other messages. Message
          frequency may vary. Please note that message and data rates may apply,
          and carriers are not responsible for any delayed or undelivered
          messages. Text HELP for help. Reply STOP to opt out. For more
          information, please review our{' '}
          <a
            href="https://soccerstars.com/agreement/"
            target="_blank"
            rel="noopener noreferrer"
            className="tw-text-blue-600 hover:tw-text-blue-800"
          >
            Terms & Conditions
          </a>{' '}
          and{' '}
          <a
            href="https://soccerstars.com/privacy-policy-2/"
            target="_blank"
            rel="noopener noreferrer"
            className="tw-text-blue-600 hover:tw-text-blue-800"
          >
            Privacy Policy
          </a>
          .
        </p>
      )}
    </div>
  );
};
export const CheckboxWithHoverLabel = ({ label, hoverLabel, ...props }) => {
  return (
    <>
      <CheckboxField
        {...props}
        labelClassName="!tw-mb-0"
        $label={
          <span className="flex tw-items-center tw-gap-1">
            <span className="text-sm">{label}</span>
            {hoverLabel && (
              <Tooltip title={hoverLabel} placement="top">
                <InfoIcon
                  sx={{
                    fontSize: 13,
                  }}
                />
              </Tooltip>
            )}
          </span>
        }
      />
    </>
  );
};

export const FranchiseSubscriptions = ({ index }) => {
  const { control, setValue } = useFormContext();
  const { update } = useFieldArray({
    name: 'franchisesOptIn',
    control,
  });

  const values = useWatch({
    name: `franchisesOptIn.${index}`,
    control,
  });

  const {
    classNotification,
    newsOrAnnouncement,
    emailNewsOrAnnouncement,
    emailClassNotification,
    name,
    isExpanded = false,
  } = values || {};

  const toggleExpanded = () => {
    update(index, { ...values, isExpanded: !isExpanded });
  };

  const smsChecked = classNotification && newsOrAnnouncement;
  const emailChecked = emailNewsOrAnnouncement && emailClassNotification;
  const allChecked = smsChecked && emailChecked;
  const franchiseIndex = `franchisesOptIn.${index}`;

  const setValues = (items, value) => {
    items.forEach((item) => {
      setValue(item, value, { shouldValidate: true });
    });
  };

  const handleAllOptIn = (e) => {
    const isChecked = e.target.checked;
    setValues(
      [
        `${franchiseIndex}.${NotificationTypes.CLASS_NOTIFICATION}`,
        `${franchiseIndex}.${NotificationTypes.NEWS_ANNOUNCEMENT}`,
        `${franchiseIndex}.${NotificationTypes.EMAIL_NEWS_ANNOUNCEMENT}`,
      ],
      isChecked,
    );
  };

  const handleSmsBulkOptIn = (e) => {
    const isChecked = e.target.checked;
    setValues(
      [
        `${franchiseIndex}.${NotificationTypes.CLASS_NOTIFICATION}`,
        `${franchiseIndex}.${NotificationTypes.NEWS_ANNOUNCEMENT}`,
      ],
      isChecked,
    );
  };
  const handleEmailBulkOptIn = (e) => {
    const isChecked = e.target.checked;
    setValues(
      [`${franchiseIndex}.${NotificationTypes.EMAIL_NEWS_ANNOUNCEMENT}`],
      isChecked,
    );
  };

  return (
    <div className="tw-mb-4 tw-border tw-border-[#a3b8c2] tw-rounded-lg tw-p-1">
      <div
        className="tw-flex tw-items-center tw-cursor-pointer tw-mb-2"
        onClick={toggleExpanded}
      >
        <IconButton size="small">
          {isExpanded ? <MdExpandMore /> : <MdChevronRight />}
        </IconButton>
        <h3 className="tw-text-base tw-font-medium">{name}</h3>
      </div>

      <Collapse in={isExpanded}>
        <div className="tw-ml-6">
          <CheckboxField
            className="tw-mb-2"
            $label="Subscribe to all Email and Text messages"
            checked={allChecked}
            onChange={handleAllOptIn}
          />
          <div className="tw-ml-6">
            <CheckboxField
              className="tw-mb-2"
              $label="Email"
              checked={emailChecked}
              onChange={handleEmailBulkOptIn}
            />
            <div className="tw-ml-6">
              <Controller
                name={`${franchiseIndex}.${NotificationTypes.EMAIL_CLASS_NOTIFICATION}`}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CheckboxWithHoverLabel
                    checked={value}
                    onChange={onChange}
                    className="tw-mb-2"
                    label="Class Updates (e.g., Time change, Progress Reports, etc.)"
                    disabled
                  />
                )}
              />
              <Controller
                name={`${franchiseIndex}.${NotificationTypes.EMAIL_NEWS_ANNOUNCEMENT}`}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CheckboxWithHoverLabel
                    checked={value}
                    onChange={onChange}
                    className="tw-mb-2"
                    label="Promotional (e.g., Newsletters, Re-enrollment, Summer Camps, etc.)"
                  />
                )}
              />
            </div>

            <CheckboxField
              className="tw-mb-2"
              $label="Text"
              checked={smsChecked}
              onChange={handleSmsBulkOptIn}
            />

            <div className="tw-ml-6">
              <Controller
                name={`${franchiseIndex}.${NotificationTypes.CLASS_NOTIFICATION}`}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CheckboxWithHoverLabel
                    checked={value}
                    onChange={onChange}
                    className="tw-mb-3"
                    label="Class Updates (e.g., Time change, Progress Reports, etc.)"
                  />
                )}
              />
              <Controller
                name={`${franchiseIndex}.${NotificationTypes.NEWS_ANNOUNCEMENT}`}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CheckboxWithConsent
                    checked={value}
                    onChange={onChange}
                    className="tw-mb-3"
                    isTextConsent
                    label="Promotional (e.g., Newsletters, Re-enrollment, Summer Camps, etc.)"
                  />
                )}
              />
            </div>
          </div>
        </div>
      </Collapse>
    </div>
  );
};

export const accountFormFieldsSchema = {
  firstName: Yup.string().label('First name').required(),
  lastName: Yup.string().label('Last name').required(),
  phone: Yup.string()
    .label('Phone number')
    .transform(Format.plainPhone)
    .required(),
  email: Yup.string().email().label('Email').required(),
  franchisesOptIn: Yup.array().of(
    Yup.object().shape({
      id: Yup.string().required(),
      name: Yup.string().required(),
      classNotification: Yup.boolean(),
      newsOrAnnouncement: Yup.boolean(),
      emailNewsOrAnnouncement: Yup.boolean(),
    }),
  ),
};

export const AccountFormFields = ({ isEdit = false }) => {
  const { register, formState, control, setValue } = useFormContext();
  const { errors, touchedFields } = formState;

  const { fields, update } = useFieldArray({
    control,
    name: 'franchisesOptIn',
  });

  const franchisesOptIn = useWatch({
    name: 'franchisesOptIn',
    control,
  });

  const newsOrAnnouncement = useWatch({
    name: NotificationTypes.NEWS_ANNOUNCEMENT,
    control,
  });
  const emailNewsOrAnnouncement = useWatch({
    name: NotificationTypes.EMAIL_NEWS_ANNOUNCEMENT,
    control,
  });
  const classNotification = useWatch({
    name: NotificationTypes.CLASS_NOTIFICATION,
    control,
  });

  const emailClassNotification = useWatch({
    name: NotificationTypes.EMAIL_CLASS_NOTIFICATION,
    control,
  });
  const handlePhoneChange = (e) => {
    const { value } = e.target;
    const formattedValue = value.replace(/\D+/g, '');
    const phoneNumber = Format.phone(formattedValue);
    e.target.value = phoneNumber;
  };

  // Calculate bulk states based on franchisesOptIn
  const bulkStates = React.useMemo(() => {
    if (!franchisesOptIn?.length) {
      return {
        classNotification: classNotification,
        emailClassNotification: emailClassNotification,
        newsOrAnnouncement: newsOrAnnouncement,
        emailNewsOrAnnouncement: emailNewsOrAnnouncement,
        smsChecked: newsOrAnnouncement && classNotification,
        emailChecked: emailNewsOrAnnouncement && emailClassNotification,
      };
    }

    return {
      classNotification: franchisesOptIn.every((f) => f.classNotification),
      newsOrAnnouncement: franchisesOptIn.every((f) => f.newsOrAnnouncement),
      emailNewsOrAnnouncement: franchisesOptIn.every(
        (f) => f.emailNewsOrAnnouncement,
      ),
      emailClassNotification: franchisesOptIn.every(
        (f) => f.emailClassNotification,
      ),
      smsChecked: franchisesOptIn.every(
        (f) => f.newsOrAnnouncement && f.classNotification,
      ),
      emailChecked: franchisesOptIn.every(
        (f) => f.emailNewsOrAnnouncement && f.emailClassNotification,
      ),
    };
  }, [
    classNotification,
    emailClassNotification,
    emailNewsOrAnnouncement,
    franchisesOptIn,
    newsOrAnnouncement,
  ]);

  const handleExpandAll = () => {
    const allExpanded = franchisesOptIn.every((f) => f.isExpanded);
    franchisesOptIn.forEach((franchise, index) => {
      update(index, { ...franchise, isExpanded: !allExpanded });
    });
  };

  const setValues = (items, value) => {
    items.forEach((item) => {
      setValue(item, value, { shouldValidate: true });
    });
  };

  const handleAllOptIn = (e) => {
    const isChecked = e.target.checked;
    if (fields.length > 0) {
      fields.forEach((_, index) => {
        setValues(
          [
            `franchisesOptIn.${index}.${NotificationTypes.CLASS_NOTIFICATION}`,
            `franchisesOptIn.${index}.${NotificationTypes.NEWS_ANNOUNCEMENT}`,
            `franchisesOptIn.${index}.${NotificationTypes.EMAIL_NEWS_ANNOUNCEMENT}`,
          ],
          isChecked,
        );
        setValues(
          [
            `franchisesOptIn.${index}.${NotificationTypes.EMAIL_CLASS_NOTIFICATION}`, // for fallback if subscriptions not found
          ],
          true,
        );
      });
      return;
    }
    setValues(
      [
        NotificationTypes.CLASS_NOTIFICATION,
        NotificationTypes.NEWS_ANNOUNCEMENT,
        NotificationTypes.EMAIL_NEWS_ANNOUNCEMENT,
      ],
      isChecked,
    );
    setValues([NotificationTypes.EMAIL_CLASS_NOTIFICATION], true); // for fallback if subscriptions not found
  };

  const handleSmsBulkOptIn = (e) => {
    const isChecked = e.target.checked;
    if (fields.length > 0) {
      fields.forEach((_, index) => {
        setValues(
          [
            `franchisesOptIn.${index}.${NotificationTypes.CLASS_NOTIFICATION}`,
            `franchisesOptIn.${index}.${NotificationTypes.NEWS_ANNOUNCEMENT}`,
          ],
          isChecked,
        );
      });
      return;
    }
    setValues(
      [
        NotificationTypes.CLASS_NOTIFICATION,
        NotificationTypes.NEWS_ANNOUNCEMENT,
      ],
      isChecked,
    );
  };

  const handleEmailBulkOptIn = (e) => {
    const isChecked = e.target.checked;
    if (fields.length > 0) {
      fields.forEach((_, index) => {
        setValues(
          [
            `franchisesOptIn.${index}.${NotificationTypes.EMAIL_NEWS_ANNOUNCEMENT}`,
          ],
          isChecked,
        );
        setValues(
          [
            `franchisesOptIn.${index}.${NotificationTypes.EMAIL_CLASS_NOTIFICATION}`, // for fallback if subscriptions not found
          ],
          true,
        );
      });
      return;
    }
    setValues([NotificationTypes.EMAIL_NEWS_ANNOUNCEMENT], isChecked);
    setValues([NotificationTypes.EMAIL_CLASS_NOTIFICATION], true); // for fallback if subscriptions not found
  };

  const handleOptIn = (type) => (e) => {
    const isChecked = e.target.checked;
    if (fields.length > 0) {
      fields.forEach((_, index) => {
        setValues([`franchisesOptIn.${index}.${type}`], isChecked);
      });
      return;
    }
    setValues([type], isChecked);
  };

  return (
    <div className="tw-space-y-4">
      <div className="flex fill mb-12">
        <InputField
          {...register('firstName')}
          className="flex-size-1"
          $label="First name"
          isTouched={touchedFields?.firstName}
          error={errors?.firstName?.message}
        />
        <InputField
          {...register('lastName')}
          className="flex-size-1 ml-12"
          $label="Last name"
          isTouched={touchedFields?.lastName}
          error={errors?.lastName?.message}
        />
      </div>

      <InputField
        {...register('phone')}
        type="tel"
        className="mb-12"
        $label="Phone number"
        isTouched={touchedFields?.phone}
        error={errors?.phone?.message}
        onChange={handlePhoneChange}
      />

      <InputField
        {...register('email')}
        type="email"
        className="tw-mb-12"
        $label="Email address"
        isTouched={touchedFields?.email}
        error={errors?.email?.message}
      />

      <div className="tw-mt-6">
        <CheckboxField
          className="tw-mb-2"
          $label="Subscribe to all Email and Text messages"
          checked={bulkStates.smsChecked && bulkStates.emailChecked}
          onChange={handleAllOptIn}
        />
        <div className="tw-ml-6">
          <CheckboxField
            className="tw-mb-2"
            $label="Email"
            checked={bulkStates.emailChecked}
            onChange={handleEmailBulkOptIn}
          />
          <div className="tw-ml-6">
            <Controller
              name={NotificationTypes.EMAIL_CLASS_NOTIFICATION}
              control={control}
              render={({ field: { onChange } }) => (
                <CheckboxWithHoverLabel
                  checked={bulkStates.emailClassNotification}
                  onChange={(e) => {
                    onChange(e);
                    handleOptIn(NotificationTypes.EMAIL_CLASS_NOTIFICATION)(e);
                  }}
                  className="tw-mb-2"
                  disabled
                  label="Class Updates (e.g., Time change, Progress Reports, etc.)"
                />
              )}
            />

            <Controller
              name={NotificationTypes.EMAIL_NEWS_ANNOUNCEMENT}
              control={control}
              render={({ field: { onChange } }) => (
                <CheckboxWithHoverLabel
                  checked={bulkStates.emailNewsOrAnnouncement}
                  onChange={(e) => {
                    onChange(e);
                    handleOptIn(NotificationTypes.EMAIL_NEWS_ANNOUNCEMENT)(e);
                  }}
                  className="tw-mb-3"
                  label="Promotional (e.g., Newsletters, Re-enrollment, Summer Camps, etc.)"
                />
              )}
            />
          </div>

          <CheckboxField
            className="tw-mb-2"
            $label="Text"
            checked={bulkStates.smsChecked}
            onChange={handleSmsBulkOptIn}
          />

          <div className="tw-ml-6">
            <Controller
              name={NotificationTypes.CLASS_NOTIFICATION}
              control={control}
              render={({ field: { onChange } }) => (
                <CheckboxWithHoverLabel
                  label="Class Updates (e.g., Time change, Progress Reports, etc.)"
                  checked={bulkStates.classNotification}
                  onChange={(e) => {
                    onChange(e);
                    handleOptIn(NotificationTypes.CLASS_NOTIFICATION)(e);
                  }}
                  className="tw-mb-2"
                />
              )}
            />
            <Controller
              name={NotificationTypes.NEWS_ANNOUNCEMENT}
              control={control}
              render={({ field: { onChange } }) => (
                <CheckboxWithConsent
                  checked={bulkStates.newsOrAnnouncement}
                  onChange={(e) => {
                    onChange(e);
                    handleOptIn(NotificationTypes.NEWS_ANNOUNCEMENT)(e);
                  }}
                  isTextConsent
                  className="tw-mb-3"
                  label="Promotional (e.g., Newsletters, Re-enrollment, Summer Camps, etc.)"
                />
              )}
            />
          </div>
        </div>

        {fields.length > 1 && (
          <div className="tw-mt-6">
            <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
              <div />
              <TextButton
                type="button"
                onClick={handleExpandAll}
                className="tw-no-underline"
              >
                {franchisesOptIn.every((f) => f.isExpanded)
                  ? 'Collapse All'
                  : 'Expand All'}
              </TextButton>
            </div>
            {fields.map((field, index) => (
              <FranchiseSubscriptions key={field.id} index={index} />
            ))}
          </div>
        )}
      </div>
      {!isEdit && (
        <div className="mb-6">
          <InputField
            {...register('password')}
            className="mb-12"
            type="password"
            $label="Password"
            isTouched={touchedFields?.password}
            error={errors?.password?.message}
          />
          <InputField
            {...register('passwordConfirmation')}
            className="mb-20"
            type="password"
            $label="Confirm password"
            isTouched={touchedFields?.passwordConfirmation}
            error={errors?.passwordConfirmation?.message}
          />
        </div>
      )}
    </div>
  );
};
